import { ActivityType, TIME_TRACKING_CONSTANTS } from '../const'
import { TimeRecordRow, WeekBreakdown } from '../types'

const isActivityType = (value: string): value is ActivityType =>
  Object.values(ActivityType).includes(value as ActivityType)

const ACTIVITY_BUCKET: Partial<
  Record<ActivityType, keyof { billable: number; nonBillable: number }>
> = {
  [ActivityType.BILLABLE]: 'billable',
  [ActivityType.NON_BILLABLE]: 'nonBillable',
}

const sumEntryHours = (entries: { hours: number }[]) =>
  entries.reduce((sum, entry) => sum + entry.hours, 0)

export const calculateDayTotal = (
  timeRecords: TimeRecordRow[],
  date: string | undefined,
): number => {
  if (!date) return 0
  const dateOnly = date.split('T')[0]
  if (!dateOnly) return 0

  return timeRecords.reduce(
    (sum, record) => sum + sumEntryHours(record.dailyEntries[dateOnly] ?? []),
    0,
  )
}

export const calculateWeekTotal = (timeRecords: TimeRecordRow[]): number =>
  timeRecords.reduce((sum, record) => sum + record.totalHours, 0)

export const calculateWeekBreakdown = (
  timeRecords: TimeRecordRow[],
): WeekBreakdown => {
  const { billable, nonBillable } = timeRecords.reduce(
    (acc, record) => {
      for (const entries of Object.values(record.dailyEntries)) {
        for (const entry of entries) {
          if (!isActivityType(entry.activityType)) continue
          const bucket = ACTIVITY_BUCKET[entry.activityType]
          if (bucket) acc[bucket] += entry.hours
        }
      }
      return acc
    },
    { billable: 0, nonBillable: 0 },
  )

  const total = billable + nonBillable
  const expected = TIME_TRACKING_CONSTANTS.EXPECTED_WEEK_HOURS
  const missing = Math.max(expected - total, 0)
  const utilization = expected > 0 ? Math.round((billable / expected) * 100) : 0

  return { billable, nonBillable, missing, total, expected, utilization }
}
